<script setup>
import { computed, inject, ref } from 'vue';
import { flowThreadContextKey } from './flowThreadContext';
import NodeBadge from '../ui/NodeBadge.vue';
import NodeDashedActionButton from '../ui/NodeDashedActionButton.vue';
import NodeIconButton from '../ui/NodeIconButton.vue';
import NodeInlineField from '../ui/NodeInlineField.vue';
import NodeQuickInsertMenu from '../ui/NodeQuickInsertMenu.vue';
import NodeTextareaField from '../ui/NodeTextareaField.vue';

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  isInteractiveListNode: {
    type: Boolean,
    default: false,
  },
  listKeyboardBranch: {
    type: Object,
    default: null,
  },
  listSections: {
    type: Array,
    default: () => [],
  },
  interactiveButtons: {
    type: Array,
    default: () => [],
  },
});

const context = inject(flowThreadContextKey);

const hasBodyValue = computed(() => Boolean(props.node.config?.body?.trim?.()));
const messageBody = computed(() => props.node.config?.body || '');
const messageMetaClass = computed(() =>
  props.isSelected ? 'text-[#0f6b5a]/80' : 'text-n-slate-10'
);
const hasButtonTextValue = computed(() =>
  Boolean(props.node.config?.buttonText?.trim?.())
);
const interactiveHeaderPreviewUrl = computed(
  () =>
    props.node.config?.header?.mediaUrl || props.node.config?.header?.mediaLink
);
const canShowFooter = computed(() => props.node.type === 'interactive');
const listRows = computed(() =>
  props.listSections.flatMap(section => section.rows || [])
);
const listButtonText = computed(() => {
  const branchButtonText =
    props.listKeyboardBranch?.item?.node?.config?.buttonText;
  return branchButtonText || context.uiCopy.viewList;
});
const listSectionCount = computed(() => props.listSections.length);
const listRowCount = computed(() => listRows.value.length);
const listSectionCountLabel = computed(
  () => `${context.uiCopy.listSectionsCounter} ${listSectionCount.value}`
);
const listRowCountLabel = computed(
  () => `${context.uiCopy.listRowsCounter} ${listRowCount.value} من 10`
);
const interactiveListCardClass = computed(() =>
  context.hasOpenQuickInsertMenu.value
    ? 'mt-2 overflow-visible rounded-[1rem] bg-[#f8faf8] shadow-[inset_0_0_0_1px_rgba(15,23,42,0.07)]'
    : 'mt-2 overflow-hidden rounded-[1rem] bg-[#f8faf8] shadow-[inset_0_0_0_1px_rgba(15,23,42,0.07)]'
);
const draggedButtonNodeId = ref('');

const handleButtonDragStart = (buttonNodeId, event) => {
  draggedButtonNodeId.value = buttonNodeId;
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', buttonNodeId);
  }
};

const handleButtonDragEnd = () => {
  draggedButtonNodeId.value = '';
};

const handleButtonDragOver = (buttonNodeId, event) => {
  if (
    !draggedButtonNodeId.value ||
    draggedButtonNodeId.value === buttonNodeId
  ) {
    return;
  }

  event.preventDefault();
  if (event?.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
};

const handleButtonDrop = buttonNodeId => {
  const sourceButtonNodeId = draggedButtonNodeId.value;
  draggedButtonNodeId.value = '';

  if (!sourceButtonNodeId || sourceButtonNodeId === buttonNodeId) {
    return;
  }

  const currentIndex = context.interactiveButtonPosition(sourceButtonNodeId);
  const targetIndex = context.interactiveButtonPosition(buttonNodeId);

  if (
    currentIndex === -1 ||
    targetIndex === -1 ||
    currentIndex === targetIndex
  ) {
    return;
  }

  const direction = currentIndex < targetIndex ? 'down' : 'up';
  const moves = Math.abs(targetIndex - currentIndex);

  Array.from({ length: moves }).forEach(() => {
    context.requestMoveInteractiveButton({
      interactiveNodeId: props.node.id,
      buttonNodeId: sourceButtonNodeId,
      direction,
    });
  });
};
</script>

<template>
  <div
    v-if="node.config?.header?.enabled && node.config?.header?.mediaUrl"
    class="h-[4.8rem] overflow-hidden bg-n-slate-2"
  >
    <img
      :src="node.config.header.mediaUrl"
      alt=""
      class="h-full w-full object-cover"
    />
  </div>
  <div
    v-else-if="node.config?.header?.enabled && node.config?.header?.text"
    class="border-b border-black/5 bg-white/40 px-3 py-2 text-[12px] font-semibold text-n-slate-12"
  >
    {{ node.config.header.text }}
  </div>
  <div class="bg-[#dcf8c6] px-3 py-2">
    <div
      v-if="isSelected"
      class="mb-2 rounded-[0.95rem] bg-white/55 px-2 py-2"
      @click.stop
    >
      <div class="grid grid-cols-3 gap-1.5">
        <button
          type="button"
          class="flex h-8 items-center justify-center rounded-xl border text-[10px] font-semibold transition"
          :class="
            !node.config?.header?.enabled
              ? 'border-[#128c7e] bg-[#128c7e] text-white shadow-sm'
              : 'border-black/10 bg-white text-n-slate-11 hover:bg-n-slate-2'
          "
          @click.stop="context.setInteractiveHeaderMode('none')"
        >
          {{ context.uiCopy.noHeader }}
        </button>
        <button
          type="button"
          class="flex h-8 items-center justify-center rounded-xl border text-[10px] font-semibold transition"
          :class="
            node.config?.header?.enabled && node.config?.header?.type === 'text'
              ? 'border-[#128c7e] bg-[#128c7e] text-white shadow-sm'
              : 'border-black/10 bg-white text-n-slate-11 hover:bg-n-slate-2'
          "
          @click.stop="context.setInteractiveHeaderMode('text')"
        >
          {{ context.uiCopy.textHeader }}
        </button>
        <button
          type="button"
          class="flex h-8 items-center justify-center rounded-xl border text-[10px] font-semibold transition"
          :class="
            node.config?.header?.enabled &&
            node.config?.header?.type === 'media'
              ? 'border-[#128c7e] bg-[#128c7e] text-white shadow-sm'
              : 'border-black/10 bg-white text-n-slate-11 hover:bg-n-slate-2'
          "
          @click.stop="context.setInteractiveHeaderMode('media')"
        >
          {{ context.uiCopy.mediaHeader }}
        </button>
      </div>

      <NodeInlineField
        v-if="
          node.config?.header?.enabled && node.config?.header?.type === 'text'
        "
        :model-value="node.config?.header?.text || ''"
        :placeholder="context.uiCopy.headerTextPlaceholder"
        variant="headerText"
        @update:model-value="context.updateInteractiveHeader({ text: $event })"
      />

      <div
        v-if="
          node.config?.header?.enabled && node.config?.header?.type === 'media'
        "
        class="mt-2"
      >
        <input
          :ref="context.interactiveHeaderUploadRef"
          type="file"
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          class="hidden"
          @change="context.handleInteractiveHeaderUpload"
        />
        <button
          type="button"
          class="flex h-9 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#25d366]/35 bg-white text-[11px] font-medium text-[#128c7e] transition hover:bg-[#25d366]/5"
          @click.stop="context.triggerInteractiveHeaderUpload"
        >
          <i class="i-lucide-upload size-4" />
          {{ context.uiCopy.mediaUpload }}
        </button>
        <div
          v-if="interactiveHeaderPreviewUrl"
          class="mt-2 overflow-hidden rounded-xl bg-n-slate-2"
        >
          <img
            v-if="node.config?.header?.mediaType === 'image'"
            :src="interactiveHeaderPreviewUrl"
            alt=""
            class="h-20 w-full object-cover"
          />
          <video
            v-else-if="node.config?.header?.mediaType === 'video'"
            :src="interactiveHeaderPreviewUrl"
            class="h-20 w-full object-cover"
            controls
          />
          <div
            v-else
            class="flex h-20 items-center justify-center text-[11px] text-n-slate-11"
          >
            {{ node.config?.header?.fileName || context.uiCopy.mediaUpload }}
          </div>
        </div>
        <p class="mt-1.5 text-[10px] text-n-slate-10">
          {{ context.uiCopy.mediaLimits }}
        </p>
      </div>
    </div>
    <div class="h-[3.5rem] overflow-hidden">
      <NodeTextareaField
        v-if="isSelected"
        :model-value="node.config?.body || ''"
        min-height="3.5rem"
        max-height="3.5rem"
        variant="fixedBubble"
        :placeholder="context.uiCopy.inlineTextPlaceholder"
        @update:model-value="context.updateNode({ body: $event })"
      />
      <p
        v-else
        class="h-[3.5rem] overflow-hidden whitespace-pre-wrap text-[12px] leading-5"
        :class="hasBodyValue ? 'text-n-slate-12' : 'text-n-slate-9'"
      >
        {{ messageBody || context.uiCopy.inlineTextPlaceholder }}
      </p>
    </div>
    <div class="mt-2 min-h-[18px] border-t border-black/5 pt-1.5">
      <NodeInlineField
        v-if="isSelected && canShowFooter"
        :model-value="node.config?.footer || ''"
        :placeholder="context.uiCopy.inlineFooterPlaceholder"
        variant="plainCaption"
        @update:model-value="context.updateNode({ footer: $event })"
      />
      <p v-else-if="node.config?.footer" class="text-[10px] text-n-slate-11">
        {{ node.config.footer }}
      </p>
    </div>

    <div class="mt-1.5 flex items-center justify-end gap-1 text-[10px]">
      <span :class="messageMetaClass">{{ context.uiCopy.timestamp }}</span>
      <i class="i-lucide-check-check text-[11px] text-[#53bdeb]" />
    </div>

    <div v-if="isInteractiveListNode" :class="interactiveListCardClass">
      <div
        class="flex h-[31px] items-center justify-between border-b border-black/5 px-3 text-[12px] font-medium text-[#128c7e]"
      >
        <NodeInlineField
          v-if="isSelected"
          :model-value="listButtonText"
          :placeholder="context.uiCopy.listButtonPlaceholder"
          variant="plainAction"
          @update:model-value="
            context.updateListKeyboardNode({ buttonText: $event })
          "
        />
        <span
          v-else
          class="truncate"
          :class="hasButtonTextValue ? 'text-[#128c7e]' : 'text-n-slate-9'"
        >
          {{ listButtonText }}
        </span>
        <i
          class="i-lucide-chevron-left rtl:rotate-180 text-sm text-n-slate-10"
        />
      </div>

      <div class="px-2.5 py-2">
        <div
          v-if="isSelected"
          class="mb-2.5 flex flex-wrap items-center gap-1.5 px-0.5"
        >
          <NodeBadge :label="listSectionCountLabel" variant="neutral" />
          <NodeBadge :label="listRowCountLabel" variant="neutral" />
        </div>

        <div
          v-for="sectionData in listSections"
          :key="sectionData.section?.id || `${node.id}_list_section`"
          class="mb-3 last:mb-0"
        >
          <div
            class="mb-1.5 flex items-center gap-1.5 border-b border-black/5 px-0.5 pb-1.5"
          >
            <div
              class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white text-n-slate-9 shadow-[inset_0_0_0_1px_rgba(15,23,42,0.06)]"
            >
              <i class="i-lucide-list size-[12px]" />
            </div>
            <div class="min-w-0 flex-1">
              <NodeInlineField
                v-if="isSelected"
                :model-value="sectionData.section?.config?.title || ''"
                :placeholder="context.uiCopy.listSectionPlaceholder"
                variant="surfaceSm"
                @update:model-value="
                  context.updateListSectionNode(sectionData.section?.id, {
                    title: $event,
                  })
                "
              />
              <div
                v-else-if="sectionData.section?.config?.title"
                class="text-[10px] font-semibold text-n-slate-10"
              >
                {{ sectionData.section.config.title }}
              </div>
              <p
                v-if="isSelected && listSectionCount > 1"
                class="mt-0.5 text-[9px] text-n-slate-9"
              >
                {{ context.uiCopy.listSectionTitleHint }}
              </p>
            </div>
            <NodeIconButton
              v-if="isSelected"
              icon="i-lucide-x"
              variant="floatingDanger"
              size="xs"
              :disabled="listSectionCount <= 1"
              @click.stop="context.requestDeleteNode(sectionData.section?.id)"
            />
          </div>

          <div
            :class="
              context.hasOpenQuickInsertMenu.value
                ? 'overflow-visible rounded-[0.95rem] bg-white/88'
                : 'overflow-hidden rounded-[0.95rem] bg-white/88'
            "
          >
            <div
              v-for="rowBranch in sectionData.rows"
              :key="rowBranch.id"
              class="relative border-b border-black/5 px-2.5 py-2.5 last:border-b-0"
            >
              <div class="flex min-w-0 items-start justify-between gap-2">
                <div class="flex min-w-0 flex-1 gap-2">
                  <div
                    class="flex h-7 w-4 flex-shrink-0 items-center justify-center text-n-slate-8"
                  >
                    <i class="i-lucide-grip-vertical size-[12px]" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <NodeInlineField
                      v-if="isSelected"
                      :model-value="rowBranch.item?.node?.config?.title || ''"
                      :placeholder="context.uiCopy.listRowTitlePlaceholder"
                      variant="plainStrong"
                      @update:model-value="
                        context.updateBranchNode(rowBranch, {
                          title: $event,
                        })
                      "
                    />
                    <div
                      v-else
                      class="truncate text-[12px] font-medium text-n-slate-12"
                    >
                      {{
                        rowBranch.item?.node?.config?.title ||
                        rowBranch.item?.node?.label
                      }}
                    </div>
                    <NodeInlineField
                      v-if="isSelected"
                      :model-value="
                        rowBranch.item?.node?.config?.description || ''
                      "
                      :placeholder="
                        context.uiCopy.listRowDescriptionPlaceholder
                      "
                      variant="plainSoft"
                      @update:model-value="
                        context.updateBranchNode(rowBranch, {
                          description: $event,
                        })
                      "
                    />
                    <div
                      v-else-if="rowBranch.item?.node?.config?.description"
                      class="mt-0.5 truncate text-[10px] text-n-slate-11"
                    >
                      {{ rowBranch.item.node.config.description }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="isSelected"
                  class="mt-0.5 flex flex-shrink-0 items-center gap-1"
                >
                  <NodeIconButton
                    icon="i-lucide-plus"
                    variant="positiveSoft"
                    size="xs"
                    :data-quick-insert-trigger="`row_${rowBranch.item?.node?.id}`"
                    :title="context.uiCopy.addAfterRow"
                    @click.stop="
                      context.toggleQuickInsertMenu(
                        `row_${rowBranch.item?.node?.id}`
                      )
                    "
                  />
                  <NodeIconButton
                    icon="i-lucide-x"
                    variant="floatingDanger"
                    size="xs"
                    :disabled="sectionData.rows.length <= 1"
                    @click.stop="
                      context.requestDeleteNode(rowBranch.item?.node?.id)
                    "
                  />
                </div>
              </div>
              <NodeBadge
                v-if="
                  rowBranch.item?.next?.node &&
                  context.quickInsertMenuTarget.value !==
                    `row_${rowBranch.item?.node?.id}`
                "
                :label="context.branchContinuationSummary(rowBranch)"
                class="mt-2 max-w-[9.5rem] cursor-pointer transition hover:bg-[#25d366]/14"
                variant="successOutline"
                @click.stop="context.selectNode(rowBranch.item?.next?.node?.id)"
              />
              <NodeQuickInsertMenu
                v-if="
                  context.quickInsertMenuTarget.value ===
                  `row_${rowBranch.item?.node?.id}`
                "
                :target-id="`row_${rowBranch.item?.node?.id}`"
                :options="context.branchInsertItems.value"
                align-class="left-2"
                width-class="w-36"
                padding-class="p-1"
                item-padding-class="px-2 py-1.5"
                item-icon-class="text-[13px]"
                @select="
                  context.insertBranchNode(
                    $event,
                    rowBranch.item?.node?.id || ''
                  )
                "
              />
            </div>
          </div>

          <div v-if="isSelected && sectionData.section?.id" class="mt-1.5 flex">
            <NodeDashedActionButton
              :label="context.uiCopy.addRow"
              class="h-8 rounded-[0.95rem] bg-white"
              :disabled="!context.canAddRowToSection(sectionData.rows)"
              @click.stop="
                context.requestAddInteractiveListRow(sectionData.section?.id)
              "
            />
          </div>
          <p
            v-if="
              isSelected && sectionData.rows.length >= 10 && listRowCount < 10
            "
            class="mt-1 px-1 text-[9px] text-n-amber-11"
          >
            {{ context.uiCopy.listRowLimitReached }}
          </p>
        </div>

        <div
          v-if="isSelected && listKeyboardBranch?.item?.node?.id"
          class="flex"
        >
          <NodeDashedActionButton
            :label="context.uiCopy.addSection"
            variant="muted"
            class="h-8 rounded-[0.95rem] bg-white"
            :disabled="!context.canAddAnotherSection.value"
            @click.stop="
              context.requestAddInteractiveListSection(
                listKeyboardBranch.item.node.id
              )
            "
          />
        </div>
        <p
          v-if="isSelected && listRowCount >= 10"
          class="mt-1 px-1 text-[9px] text-n-amber-11"
        >
          {{ context.uiCopy.listTotalLimitReached }}
        </p>
      </div>
    </div>
  </div>

  <div
    v-if="interactiveButtons.length"
    class="rounded-b-[1.25rem] border-t border-black/5 bg-white px-2 py-1.5"
  >
    <div
      v-for="branch in interactiveButtons"
      :key="branch.id"
      class="relative last:mb-0"
      :class="
        context.quickInsertMenuTarget.value ===
        `button_${branch.item?.node?.id}`
          ? 'mb-0'
          : 'mb-1.5'
      "
    >
      <div
        class="flex items-center gap-1.5 rounded-[0.95rem] border border-black/5 bg-[#f8fdf8] px-1.5 py-1"
        :class="
          draggedButtonNodeId === branch.item?.node?.id
            ? 'opacity-75 ring-1 ring-[#25d366]/35'
            : ''
        "
        @dragover="handleButtonDragOver(branch.item?.node?.id, $event)"
        @drop.prevent="handleButtonDrop(branch.item?.node?.id)"
      >
        <div
          class="flex h-[30px] w-5 flex-shrink-0 items-center justify-center rounded-lg text-n-slate-9 transition hover:bg-black/5"
          draggable="true"
          :title="context.uiCopy.reorderButtons || 'إعادة الترتيب'"
          @dragstart="handleButtonDragStart(branch.item?.node?.id, $event)"
          @dragend="handleButtonDragEnd"
        >
          <i class="i-lucide-grip-vertical size-[14px]" />
        </div>
        <div
          class="flex h-[30px] min-w-0 flex-1 items-center rounded-[0.85rem] bg-white px-2.5 text-[12px] font-medium text-[#128c7e] shadow-[inset_0_0_0_1px_rgba(18,140,126,0.18)] transition focus-within:shadow-[inset_0_0_0_1px_rgba(37,211,102,0.55),0_0_0_2px_rgba(37,211,102,0.12)]"
        >
          <NodeInlineField
            :model-value="context.buttonDraftValue(branch)"
            :placeholder="context.uiCopy.branchPlaceholder"
            variant="buttonLabelCompact"
            @update:model-value="context.updateButtonDraft(branch, $event)"
            @blur="context.persistButtonDraft(branch)"
          />
        </div>
        <div class="flex flex-shrink-0 items-center gap-1">
          <NodeIconButton
            icon="i-lucide-plus"
            variant="positiveSoft"
            size="xs"
            :data-quick-insert-trigger="`button_${branch.item?.node?.id}`"
            :title="context.uiCopy.addAfterButton"
            @click.stop="
              context.toggleQuickInsertMenu(`button_${branch.item?.node?.id}`)
            "
          />
          <NodeIconButton
            icon="i-lucide-x"
            variant="floatingDanger"
            size="xs"
            :disabled="interactiveButtons.length <= 1"
            @click.stop="context.requestDeleteNode(branch.item?.node?.id)"
          />
        </div>
      </div>
      <NodeQuickInsertMenu
        v-if="
          context.quickInsertMenuTarget.value ===
          `button_${branch.item?.node?.id}`
        "
        :target-id="`button_${branch.item?.node?.id}`"
        :options="context.branchInsertItems.value"
        align-class="left-0"
        @select="context.insertBranchNode($event, branch.item?.node?.id || '')"
      />
    </div>
    <NodeDashedActionButton
      :label="context.uiCopy.addButton"
      class="h-[30px] text-[12px]"
      :class="context.hasOpenInteractiveButtonMenu.value ? 'mt-0.5' : 'mt-1'"
      :disabled="!context.canAddInteractiveButton.value"
      @click.stop="context.requestAddInteractiveButton(node.id)"
    />
  </div>
</template>
